import React, { useRef, useState } from 'react';
import { CrossIcon, DownArrowIcon, PlusIcon } from 'assets/icons';
import { Popover } from '@headlessui/react';
import AmountFilterPopoverPanel from 'components/custom-amount-filter/AmountFilterPopoverPanel';
import DropdownPopoverPanel from 'components/dropdown-with-search/DropdownPopoverPanel';
import CustomAmountFilter, { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { AmountFilterOperation } from 'constants/common';
import LocationFilterPopover, { LocationFilter } from 'components/location-filter/LocationFilterPopover';
import If from 'components/If/If';

export enum FilterDropdownType {
  DROPDOWN = 'dropdown',
  AMOUNT = 'amount',
  DATE = 'date',
  TEXT = 'text',
  LOCATION = 'location'
}

export interface FilterOption {
  id: string;
  label: string;
  type: FilterDropdownType;
  idKey?: string;
  nameKey?: string;
  showSearchInFilter?: boolean;
}

export interface AppliedFilter extends FilterOption {
  value: string[] | AmountFilter | LocationFilter;
  displayValue: string;
}

interface MoreFiltersProps {
  onFilterApply: (filters: {
    selectedFilterItems: string[] | AmountFilter | LocationFilter;
    filter: FilterOption;
  }) => void;
  onFilterRemove: (filterId: string) => void;
  appliedFilters: AppliedFilter[];
  filterOptions: FilterOption[];
  className?: string;
  getFilterData?: (filterId: string) => Array<Record<string, any>>;
  buttonClassName?: string;
}

const MoreFilters: React.FC<MoreFiltersProps> = ({
  onFilterApply,
  onFilterRemove,
  appliedFilters = [],
  filterOptions,
  getFilterData,
  buttonClassName = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterOption | null>(null);
  const [activeFilterId, setActiveFilterId] = useState<string | null>(null);
  const moreFiltersButtonRef = useRef<HTMLButtonElement>(null);
  const appliedFilterButtonRef = useRef<HTMLButtonElement>(null);
  const [amountOperation, setAmountOperation] = useState<AmountFilterOperation>(AmountFilterOperation.ALL);
  const [startValue, setStartValue] = useState<number | null>(null);
  const [endValue, setEndValue] = useState<number | null>(null);
  const [amountFilter, setAmountFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.BW,
    minAmount: null,
    maxAmount: null
  });

  const [selectedDraftItems, setSelectedDraftItems] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>('');

  const getFilterOptions = (filterId: string) => {
    if (getFilterData) {
      return getFilterData(filterId);
    }
    return [];
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedFilter(null);
    setActiveFilterId(null);
    setSelectedDraftItems([]);
    setSearchText('');
  };

  const handleFilterSelect = (filter: FilterOption) => {
    setSelectedFilter(filter);
  };

  const handleEditFilter = (filter: AppliedFilter) => {
    const filterOption = filterOptions.find(option => option.id === filter.id);
    if (!filterOption) return;

    setSelectedFilter(filterOption);
    setActiveFilterId(filter.id);
    if (filterOption.type === FilterDropdownType.DROPDOWN) {
      const values = filter.value as string[];
      setSelectedDraftItems(values);
    }
  };

  const handleRemoveFilter = (filterId: string) => {
    onFilterRemove(filterId);
  };

  const handleOpenMoreFilters = () => {
    setIsOpen(true);
    setSelectedFilter(null);
  };

  const handleApplyFilter = (selectedFilterItems: string[], filter: FilterOption) => {
    onFilterApply({ selectedFilterItems, filter });
  };

  const handleApplyAmountFilter = (amount: AmountFilter) => {
    setAmountFilter(amount);
    if (selectedFilter) {
      onFilterApply({ selectedFilterItems: amount, filter: selectedFilter });
    } else {
      onFilterRemove(filterOptions.find(option => option.type === FilterDropdownType.AMOUNT)?.id || '');
    }
  };

  const handleApplyLocationFilter = (location: LocationFilter) => {
    onFilterApply({ selectedFilterItems: location, filter: selectedFilter });
    handleClose();
  };

  const getAvailableFilterOptions = () => {
    return filterOptions.filter(option => !appliedFilters.some(filter => filter.id === option.id));
  };

  const renderFilterPopover = (filter: AppliedFilter) => {
    if (!selectedFilter || filter.id !== activeFilterId) return null;

    return (
      <div
        className="absolute z-50 mt-2 rounded-md border border-secondaryBtn bg-white shadow-lg"
        style={{ minWidth: '208px' }}>
        {selectedFilter.type === 'amount' && (
          <div className="pt-3">
            <AmountFilterPopoverPanel
              operation={amountOperation}
              setOperation={setAmountOperation}
              startValue={startValue}
              setStartValue={setStartValue}
              endValue={endValue}
              setEndValue={setEndValue}
              amountFilter={amountFilter}
              setAmountFilter={handleApplyAmountFilter}
              close={handleClose}
            />
          </div>
        )}
        {selectedFilter.type === 'location' && (
          <div className="pt-3">
            <LocationFilterPopover
              anchorEl={appliedFilterButtonRef.current}
              isOpen={true}
              onClose={handleClose}
              onApply={handleApplyLocationFilter}
              initialLocation={filter.value as LocationFilter}
            />
          </div>
        )}
        {selectedFilter.type === 'dropdown' && (
          <div className="pt-3">
            <DropdownPopoverPanel
              data={getFilterOptions(selectedFilter.id)}
              dataKey={selectedFilter.nameKey || 'name'}
              selectedDraftArray={selectedDraftItems}
              setSelectedDraftArray={setSelectedDraftItems}
              setSelectedArray={(items: string[]) => handleApplyFilter(items, selectedFilter)}
              searchText={searchText}
              setSearchText={setSearchText}
              dataIdKey={selectedFilter.idKey || 'id'}
              filterPlaceholderText={`Select ${selectedFilter.label.toLowerCase()}`}
              filterPlaceholderStyle="text-gray-500"
              searchPlaceHolderText={`Search ${selectedFilter.label.toLowerCase()}`}
              searchInputStyle="w-full p-2 border rounded"
              allowSearch={selectedFilter.showSearchInFilter ?? true}
              showResultsCount={true}
              filterId={selectedFilter.id}
              secondaryDataKey=""
              searchFilterId={`search-${selectedFilter.id}`}
              close={handleClose}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Applied Filters */}
      {appliedFilters.map(filter => (
        <div key={filter.id} className="relative flex shrink-0 items-center">
          {filter.type === FilterDropdownType.AMOUNT ? (
            <CustomAmountFilter
              amountFilter={filter.value as AmountFilter}
              setAmountFilter={handleApplyAmountFilter}
              handleDropdownOpen={() => handleEditFilter(filter)}
              handleRemoveFilter={() => handleRemoveFilter(filter.id)}
            />
          ) : (
            <Popover>
              <Popover.Button>
                <button
                  ref={appliedFilterButtonRef}
                  className={`flex items-center 
                    gap-x-2 rounded-md border border-secondary bg-secondaryBtn  
                    px-3 py-1.5 text-base font-semibold text-primaryText shadow 
                   outline-none transition-all duration-150 ease-linear focus:outline-none
                   ${buttonClassName}`}
                  aria-label={`Edit ${filter.label} filter`}
                  tabIndex={0}
                  onClick={() => handleEditFilter(filter)}>
                  <CrossIcon
                    onClick={e => {
                      e.stopPropagation();
                      handleRemoveFilter(filter.id);
                    }}
                  />
                  <span>{filter.label}</span>
                  <span> {' | '} </span>
                  <span className={'text-secondary'}>{filter.displayValue}</span>
                  <DownArrowIcon
                    className={isOpen && activeFilterId === filter.id ? 'shrink-0 rotate-180' : 'shrink-0'}
                  />
                </button>
              </Popover.Button>
              <Popover.Panel>{renderFilterPopover(filter)}</Popover.Panel>
            </Popover>
          )}
        </div>
      ))}

      {/* More Filters Button */}
      <If condition={appliedFilters.length !== filterOptions.length}>
        <Popover className="relative">
          <Popover.Button
            ref={moreFiltersButtonRef}
            className={`flex shrink-0 items-center 
                gap-x-2 rounded-md border border-dashed border-border bg-primary  
                px-3 py-1.5 text-base font-semibold text-primaryText 
               outline-none transition-all duration-150 ease-linear focus:outline-none
               ${buttonClassName}`}
            onClick={handleOpenMoreFilters}
            aria-label="More filters"
            tabIndex={0}>
            <PlusIcon className="shrink-0" />
            <span>More filters</span>
            <DownArrowIcon className={isOpen ? 'shrink-0 rotate-180' : 'shrink-0'} />
          </Popover.Button>

          {isOpen && (
            <Popover.Panel
              className="absolute z-50 mt-2 rounded-md border border-border bg-white"
              style={{ minWidth: '208px' }}>
              {!selectedFilter ? (
                <div className="flex flex-col px-3 py-1.5">
                  {getAvailableFilterOptions().map(option => (
                    <div
                      key={option.id}
                      className="flex cursor-pointer items-center gap-x-2.5 rounded py-1.5 hover:bg-gray-50"
                      onClick={() => handleFilterSelect(option)}
                      onKeyDown={e => e.key === 'Enter' && handleFilterSelect(option)}
                      tabIndex={0}
                      aria-label={`Select ${option.label} filter`}>
                      <PlusIcon className="h-4 w-4 shrink-0" />
                      <span className="text-base font-semibold text-primaryText">{option.label}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {selectedFilter.type === FilterDropdownType.AMOUNT && (
                    <div className="pt-3">
                      <AmountFilterPopoverPanel
                        operation={amountOperation}
                        setOperation={setAmountOperation}
                        startValue={startValue}
                        setStartValue={setStartValue}
                        endValue={endValue}
                        setEndValue={setEndValue}
                        amountFilter={amountFilter}
                        setAmountFilter={handleApplyAmountFilter}
                        close={handleClose}
                      />
                    </div>
                  )}
                  {selectedFilter.type === FilterDropdownType.LOCATION && (
                    <div className="pt-3">
                      <LocationFilterPopover
                        anchorEl={moreFiltersButtonRef.current}
                        isOpen={true}
                        onClose={handleClose}
                        onApply={handleApplyLocationFilter}
                        initialLocation={{ zipCode: '', rangeInMiles: 0 }}
                      />
                    </div>
                  )}
                  {selectedFilter.type === FilterDropdownType.DROPDOWN && (
                    <div className="pt-3">
                      <DropdownPopoverPanel
                        data={getFilterOptions(selectedFilter.id)}
                        dataKey={selectedFilter.nameKey || 'name'}
                        selectedDraftArray={selectedDraftItems}
                        setSelectedDraftArray={setSelectedDraftItems}
                        setSelectedArray={(items: string[]) => handleApplyFilter(items, selectedFilter)}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        dataIdKey={selectedFilter.idKey || 'id'}
                        filterPlaceholderText={`Select ${selectedFilter.label.toLowerCase()}`}
                        filterPlaceholderStyle="text-gray-500"
                        searchPlaceHolderText={`Search ${selectedFilter.label.toLowerCase()}`}
                        searchInputStyle="w-full p-2 border rounded"
                        allowSearch={selectedFilter.showSearchInFilter ?? true}
                        showResultsCount={true}
                        filterId={selectedFilter.id}
                        secondaryDataKey=""
                        searchFilterId={`search-${selectedFilter.id}`}
                        close={handleClose}
                      />
                    </div>
                  )}
                </>
              )}
            </Popover.Panel>
          )}
        </Popover>
      </If>
    </>
  );
};

export default MoreFilters;
